<template>
  <div>
    <!-- Header -->
    <h4 class="mt-2 mb-1">{{ $t('golf.hole') }} {{ hole.holeNumber }}<span v-if="isAdmin"> — {{ $t('golf.detailedAnalysis') }}</span></h4>
    <small class="text-muted" v-if="hole.greenInfo">
      {{ $t('golf.area') }}: {{ hole.greenInfo.areaM2 }} m&sup2;
      <span v-if="hole.par"> &middot; {{ $t('golf.par') }} {{ hole.par }}</span>
    </small>

    <!-- ===== REGULAR USER: Mobile-first data entry ===== -->
    <div v-if="!isAdmin" class="mt-3">
      <!-- Last values summary -->
      <div class="row g-2 mb-4">
        <div class="col-6">
          <div class="card bg-light border-0 shadow-sm h-100">
            <div class="card-body text-center py-3">
              <div class="text-success fw-bold" style="font-size: 2rem;">{{ latestClippingMlPerM2?.toFixed(1) || '–' }}</div>
              <div class="small fw-bold">{{ $t('golf.clippingsMlPerM2') }}</div>
              <small v-if="latestClipping != null" class="text-muted">{{ latestClipping.toFixed(1) }} L</small>
            </div>
          </div>
        </div>
        <div class="col-6">
          <div class="card bg-light border-0 shadow-sm h-100">
            <div class="card-body text-center py-3">
              <div class="text-primary fw-bold" style="font-size: 2rem;">{{ latestStimp?.toFixed(1) || '–' }}</div>
              <div class="small fw-bold">Stimp (ft)</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Record measurements - large, touch-friendly -->
      <div class="card mb-4">
        <div class="card-body">
          <h6 class="fw-bold mb-3">{{ $t('golf.recordMeasurement') }}</h6>

          <!-- Clipping -->
          <div class="mb-4 pb-3 border-bottom">
            <label class="form-label fw-bold mb-1">{{ $t('golf.clippingsLiters') }}</label>
            <div class="input-group input-group-lg mb-1">
              <input v-model="newClipping" type="text" inputmode="decimal" class="form-control" placeholder="0.0" />
              <button class="btn btn-success px-4" @click="submitClipping">
                <i class="material-icons" style="vertical-align:middle;">save</i>
              </button>
            </div>
            <input v-model="newClippingDate" type="date" class="form-control form-control-sm" />
          </div>

          <!-- Stimp -->
          <div class="mb-4 pb-3 border-bottom">
            <label class="form-label fw-bold mb-1">{{ $t('golf.stimpCm') }}</label>
            <div class="input-group input-group-lg mb-1">
              <input v-model="newStimp" type="text" inputmode="decimal" class="form-control" placeholder="0.0" />
              <span class="input-group-text">cm</span>
              <button class="btn btn-primary px-4" @click="submitStimp">
                <i class="material-icons" style="vertical-align:middle;">save</i>
              </button>
            </div>
            <input v-model="newStimpDate" type="date" class="form-control form-control-sm" />
          </div>

          <!-- Fertilizer -->
          <div>
            <label class="form-label fw-bold mb-1">{{ $t('golf.fertilizer') }}</label>
            <select v-model="newFertProduct" class="form-select form-select-lg mb-1">
              <option value="">-- {{ $t('golf.selectProduct') }} --</option>
              <option v-for="p in fertilizerProducts" :key="p.id" :value="p.name">{{ p.name }}</option>
            </select>
            <div class="input-group input-group-lg mb-1">
              <input v-model="newFertAmount" type="text" inputmode="decimal" class="form-control" :placeholder="$t('golf.amountKg')" />
              <button class="btn btn-warning px-4" @click="submitFertilizer">
                <i class="material-icons" style="vertical-align:middle;">save</i>
              </button>
            </div>
            <input v-model="newFertDate" type="date" class="form-control form-control-sm" />
          </div>
        </div>
      </div>

      <!-- Recent readings — simple compact list -->
      <div class="card">
        <div class="card-body">
          <h6 class="fw-bold mb-3">{{ $t('golf.recentReadings') }}</h6>
          <div class="row">
            <div class="col-6">
              <div class="fw-bold small text-muted mb-2">{{ $t('golf.clippings') }}</div>
              <editable-reading-list
                :readings="clippings"
                value-field="liters"
                unit="L"
                @update="$emit('update-clipping', $event)"
                @delete="$emit('delete-clipping', $event)"
              />
            </div>
            <div class="col-6">
              <div class="fw-bold small text-muted mb-2">Stimp</div>
              <editable-reading-list
                :readings="stimpReadings"
                value-field="speedFeet"
                unit="ft"
                edit-in-cm
                @update="$emit('update-stimp', $event)"
                @delete="$emit('delete-stimp', $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- ===== END REGULAR USER VIEW ===== -->

    <!-- ===== ADMIN VIEW: Full charts + data entry ===== -->
    <div v-if="isAdmin">

    <!-- Row 1: Four Stat Cards -->
    <div class="row mb-4 mt-3">
      <div class="col-md-3 col-6 mb-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body text-center">
            <div class="text-success fw-bold" style="font-size: 2rem;">
              {{ latestClippingMlPerM2?.toFixed(1) || '–' }}
            </div>
            <div class="fw-bold small">{{ $t('golf.clippings') }} {{ $t('golf.today') }} (mL/m&sup2;)</div>
            <small class="text-muted" v-if="latestClipping != null">
              {{ latestClipping.toFixed(1) }} L
            </small>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body text-center">
            <div class="text-primary fw-bold" style="font-size: 2rem;">
              {{ latestStimp?.toFixed(1) || '–' }}
            </div>
            <div class="fw-bold small">{{ $t('golf.stimpReading') }}</div>
            <small class="text-muted">{{ $t('golf.ballRollSpeed') }}</small>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body text-center">
            <div class="text-dark fw-bold" style="font-size: 2rem;">
              {{ avgClippingMlPerM2?.toFixed(1) || '–' }}
            </div>
            <div class="fw-bold small">{{ $t('golf.average') }} {{ $t('golf.clippings').toLowerCase() }} (mL/m&sup2;)</div>
            <small class="text-muted">{{ $t('golf.lastMeasurements', { n: selectedPeriod }) }}</small>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body text-center">
            <div class="text-dark fw-bold" style="font-size: 2rem;">
              {{ stats.stimpAvg?.toFixed(1) || '–' }}
            </div>
            <div class="fw-bold small">{{ $t('golf.average') }} {{ $t('golf.stimp').toLowerCase() }}</div>
            <small class="text-muted">{{ $t('golf.lastMeasurements', { n: selectedPeriod }) }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Row 2: Clippings History -->
    <div class="card mb-4">
      <div class="card-body">
        <h6 class="fw-bold mb-1">{{ $t('golf.clippingHistory') }} ({{ $t('golf.lastDaysCount', { n: selectedPeriod }) }})</h6>
        <small class="text-muted d-block mb-3">{{ $t('golf.measurementsAndTrend') }}</small>

        <div class="row mb-3">
          <div class="col-md-3 col-6 mb-2" v-for="period in clippingPeriods" :key="'clip-' + period.days">
            <div class="border rounded p-2" :style="{ borderLeft: '4px solid ' + period.color + ' !important' }">
              <div class="fw-bold small">{{ period.days }} {{ $t('golf.days') }}</div>
              <div class="d-flex justify-content-between">
                <span class="small text-muted">{{ $t('golf.mean') }}:</span>
                <span class="small fw-bold">{{ period.avg }}</span>
              </div>
              <div class="d-flex justify-content-between">
                <span class="small text-muted">&sigma;:</span>
                <span class="small">{{ period.stddev }}</span>
              </div>
              <div class="d-flex justify-content-between">
                <span class="small text-muted">{{ $t('golf.count') }}:</span>
                <span class="small">{{ period.count }}</span>
              </div>
            </div>
          </div>
        </div>

        <div style="position: relative; height: 250px;">
          <canvas ref="clippingsChart"></canvas>
        </div>
      </div>
    </div>

    <!-- Row 3: Stimp History -->
    <div class="card mb-4">
      <div class="card-body">
        <h6 class="fw-bold mb-1">{{ $t('golf.stimpHistory') }} ({{ $t('golf.lastDaysCount', { n: selectedPeriod }) }})</h6>
        <small class="text-muted d-block mb-3">{{ $t('golf.stimpTrend') }}</small>

        <div class="row mb-3">
          <div class="col-md-3 col-6 mb-2" v-for="period in stimpPeriods" :key="'stimp-' + period.days">
            <div class="border rounded p-2" :style="{ borderLeft: '4px solid ' + period.color + ' !important' }">
              <div class="fw-bold small">{{ period.days }} {{ $t('golf.days') }}</div>
              <div class="d-flex justify-content-between">
                <span class="small text-muted">{{ $t('golf.mean') }}:</span>
                <span class="small fw-bold">{{ period.avg }}</span>
              </div>
              <div class="d-flex justify-content-between">
                <span class="small text-muted">&sigma;:</span>
                <span class="small">{{ period.stddev }}</span>
              </div>
              <div class="d-flex justify-content-between">
                <span class="small text-muted">{{ $t('golf.count') }}:</span>
                <span class="small">{{ period.count }}</span>
              </div>
            </div>
          </div>
        </div>

        <div style="position: relative; height: 250px;">
          <canvas ref="stimpChart"></canvas>
        </div>
      </div>
    </div>

    <!-- Row 3b: Edit recent readings -->
    <div class="card mb-4">
      <div class="card-body">
        <h6 class="fw-bold mb-3">{{ $t('golf.editReadings') }}</h6>
        <div class="row">
          <div class="col-md-6">
            <div class="fw-bold small text-muted mb-2">{{ $t('golf.clippings') }}</div>
            <editable-reading-list
              :readings="clippings"
              value-field="liters"
              unit="L"
              :limit="10"
              @update="$emit('update-clipping', $event)"
              @delete="$emit('delete-clipping', $event)"
            />
          </div>
          <div class="col-md-6">
            <div class="fw-bold small text-muted mb-2">{{ $t('golf.stimp') }} ({{ $t('golf.stimpCm') }})</div>
            <editable-reading-list
              :readings="stimpReadings"
              value-field="speedFeet"
              unit="ft"
              edit-in-cm
              :limit="10"
              @update="$emit('update-stimp', $event)"
              @delete="$emit('delete-stimp', $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Row 4: Fertilizer History Table -->
    <div class="card mb-4">
      <div class="card-body">
        <h6 class="fw-bold mb-3">{{ $t('golf.fertilizer') }} - {{ $t('golf.history') }}</h6>
        <div class="table-responsive">
          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th>{{ $t('common.date') }}</th>
                <th>{{ $t('golf.productName') }}</th>
                <th>{{ $t('golf.amountKg') }}</th>
                <th>{{ $t('golf.notes') }}</th>
                <th>{{ $t('common.user') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in fertilizers" :key="f.id || f.appliedAt">
                <td>{{ formatDate(f.appliedAt) }}</td>
                <td>{{ f.productName }}</td>
                <td>{{ f.amountKg }} kg</td>
                <td>{{ f.notes || '–' }}</td>
                <td>{{ f.recordedByName || '–' }}</td>
              </tr>
              <tr v-if="!fertilizers.length">
                <td colspan="5" class="text-center text-muted py-3">{{ $t('golf.noRecords') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Quick Record -->
    <div class="card">
      <div class="card-body">
        <h6 class="fw-bold mb-3">{{ $t('golf.recordMeasurement') }}</h6>
        <div class="row g-2 mb-3">
          <!-- Clipping -->
          <div class="col-md-3">
            <label class="form-label small mb-0">{{ $t('golf.clippingsLiters') }}</label>
            <input v-model="newClipping" type="text" inputmode="decimal" class="form-control form-control-sm" />
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-0">{{ $t('common.date') }}</label>
            <input v-model="newClippingDate" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <button class="btn btn-success btn-sm w-100" @click="submitClipping">{{ $t('golf.recordClipping') }}</button>
          </div>
        </div>
        <div class="row g-2 mb-3">
          <!-- Stimp -->
          <div class="col-md-3">
            <label class="form-label small mb-0">{{ $t('golf.stimpCm') }}</label>
            <div class="input-group input-group-sm">
              <input v-model="newStimp" type="text" inputmode="decimal" class="form-control form-control-sm" />
              <span class="input-group-text">cm</span>
            </div>
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-0">{{ $t('common.date') }}</label>
            <input v-model="newStimpDate" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <button class="btn btn-info btn-sm w-100" @click="submitStimp">{{ $t('golf.recordStimp') }}</button>
          </div>
        </div>
        <div class="row g-2">
          <!-- Fertilizer -->
          <div class="col-md-3">
            <label class="form-label small mb-0">{{ $t('golf.productName') }}</label>
            <select v-model="newFertProduct" class="form-select form-select-sm">
              <option value="">-- {{ $t('golf.selectProduct') }} --</option>
              <option v-for="p in fertilizerProducts" :key="p.id" :value="p.name">{{ p.name }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-0">{{ $t('golf.amountKg') }}</label>
            <input v-model="newFertAmount" type="text" inputmode="decimal" class="form-control form-control-sm" />
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-0">{{ $t('common.date') }}</label>
            <input v-model="newFertDate" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <button class="btn btn-warning btn-sm w-100" @click="submitFertilizer">{{ $t('golf.recordFertilizer') }}</button>
          </div>
        </div>
      </div>
    </div>

    </div>
    <!-- ===== END ADMIN VIEW ===== -->

  </div>
</template>

<script>
import Chart from "chart.js/auto";
import EditableReadingList from "./EditableReadingList.vue";
import { clippingMlPerM2 } from "../../utils/clippings";

const CM_PER_FOOT = 30.48;

export default {
  components: { EditableReadingList },
  props: {
    hole: { type: Object, default: () => ({}) },
    clippings: { type: Array, default: () => [] },
    stimpReadings: { type: Array, default: () => [] },
    fertilizers: { type: Array, default: () => [] },
    fertilizerProducts: { type: Array, default: () => [] },
    stats: { type: Object, default: () => ({}) },
    selectedPeriod: { type: Number, default: 28 },
    isAdmin: { type: Boolean, default: true },
  },
  emits: [
    'record-clipping', 'record-stimp', 'record-fertilizer',
    'update-clipping', 'delete-clipping', 'update-stimp', 'delete-stimp',
  ],
  data() {
    return {
      newClipping: null,
      newClippingDate: new Date().toISOString().slice(0, 10),
      newStimp: null,
      newStimpDate: new Date().toISOString().slice(0, 10),
      newFertProduct: '',
      newFertAmount: null,
      newFertDate: new Date().toISOString().slice(0, 10),
    };
  },
  computed: {
    latestClipping() {
      if (!this.clippings.length) return null;
      const sorted = [...this.clippings].sort((a, b) => new Date(b.recordedAt) - new Date(a.recordedAt));
      return sorted[0].liters;
    },
    latestStimp() {
      if (!this.stimpReadings.length) return null;
      const sorted = [...this.stimpReadings].sort((a, b) => new Date(b.recordedAt) - new Date(a.recordedAt));
      return sorted[0].speedFeet;
    },
    latestClippingMlPerM2() {
      return clippingMlPerM2(this.latestClipping, this.hole.greenInfo?.areaM2);
    },
    avgClippingMlPerM2() {
      return clippingMlPerM2(this.stats.clippingsAvg, this.hole.greenInfo?.areaM2);
    },
    // Clippings normalised by green area — what the period stats and history chart show.
    clippingsMlPerM2() {
      const areaM2 = this.hole.greenInfo?.areaM2;
      return this.clippings
        .map(c => ({ recordedAt: c.recordedAt, mlPerM2: clippingMlPerM2(c.liters, areaM2) }))
        .filter(c => c.mlPerM2 != null);
    },
    clippingPeriods() {
      return this.computePeriods(this.clippingsMlPerM2, 'recordedAt', 'mlPerM2');
    },
    stimpPeriods() {
      return this.computePeriods(this.stimpReadings, 'recordedAt', 'speedFeet');
    },
  },
  watch: {
    clippings: {
      handler() { this.renderCharts(); },
      flush: 'post',
    },
    stimpReadings: {
      handler() { this.renderCharts(); },
      flush: 'post',
    },
    isAdmin: {
      handler() { this.$nextTick(() => this.renderCharts()); },
    },
  },
  mounted() {
    // Store chart instances as plain non-reactive properties so Vue's proxy never wraps them
    this._clippingsChart = null;
    this._stimpChart = null;
    this.$nextTick(() => this.renderCharts());
  },
  beforeUnmount() {
    if (this._clippingsChart) { this._clippingsChart.stop(); this._clippingsChart.destroy(); this._clippingsChart = null; }
    if (this._stimpChart) { this._stimpChart.stop(); this._stimpChart.destroy(); this._stimpChart = null; }
  },
  methods: {
    submitClipping() {
      const value = parseFloat(this.newClipping);
      if (!value) return;
      this.$emit('record-clipping', { value, date: this.newClippingDate });
      this.newClipping = null;
      this.newClippingDate = new Date().toISOString().slice(0, 10);
    },
    submitStimp() {
      const cm = parseFloat(this.newStimp);
      if (!cm) return;
      // Input is in centimetres; stored and displayed in feet.
      const value = cm / CM_PER_FOOT;
      this.$emit('record-stimp', { value, date: this.newStimpDate });
      this.newStimp = null;
      this.newStimpDate = new Date().toISOString().slice(0, 10);
    },
    submitFertilizer() {
      const amount = parseFloat(this.newFertAmount);
      if (!this.newFertProduct || !amount) return;
      this.$emit('record-fertilizer', { productName: this.newFertProduct, amountKg: amount, date: this.newFertDate });
      this.newFertProduct = '';
      this.newFertAmount = null;
      this.newFertDate = new Date().toISOString().slice(0, 10);
    },
    computePeriods(data, dateField, valueField) {
      const colors = ['#28a745', '#007bff', '#fd7e14', '#dc3545'];
      const periods = [7, 14, 21, 28];
      const now = new Date();
      return periods.map((days, i) => {
        const cutoff = new Date(now.getTime() - days * 86400000);
        const filtered = data.filter(d => new Date(d[dateField]) >= cutoff);
        const values = filtered.map(d => d[valueField]);
        const count = values.length;
        const avg = count ? (values.reduce((a, b) => a + b, 0) / count).toFixed(1) : '–';
        let stddev = '–';
        if (count > 1) {
          const mean = values.reduce((a, b) => a + b, 0) / count;
          stddev = Math.sqrt(values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / count).toFixed(2);
        }
        return { days, avg, stddev, count, color: colors[i] };
      });
    },
    renderCharts() {
      if (!this.isAdmin) return;
      this.renderLineChart('clippingsChart', '_clippingsChart', this.clippingsMlPerM2, 'recordedAt', 'mlPerM2', 'mL/m²', 'rgba(40, 167, 69, 0.8)');
      this.renderLineChart('stimpChart', '_stimpChart', this.stimpReadings, 'recordedAt', 'speedFeet', 'ft', 'rgba(0, 123, 255, 0.8)');
    },
    renderLineChart(refName, chartProp, data, dateField, valueField, unit, color) {
      const canvas = this.$refs[refName];
      if (!canvas || !canvas.isConnected) return;

      // Plain arrays — never pass Vue reactive arrays directly into Chart.js
      const sorted = [...data].sort((a, b) => new Date(a[dateField]) - new Date(b[dateField]));
      const labels = sorted.map(d => this.formatDate(d[dateField]));
      const values = sorted.map(d => d[valueField]);

      if (this[chartProp]) {
        // Update in place — no destroy, no RAF timing issues
        this[chartProp].data.labels = labels;
        this[chartProp].data.datasets[0].data = values;
        this[chartProp].update('none');
        return;
      }

      if (!data.length) return;

      // Chart stored as plain instance property, never in Vue data() — keeps it out of Vue's reactive proxy
      this[chartProp] = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels,
          datasets: [{
            data: values,
            borderColor: color,
            backgroundColor: color.replace('0.8', '0.1'),
            fill: true,
            tension: 0.3,
            pointRadius: 4,
            pointHoverRadius: 6,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, title: { display: true, text: unit } },
            x: { ticks: { maxTicksLimit: 10 } },
          },
        },
      });
    },
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('is-IS', { day: '2-digit', month: '2-digit' });
    },
  },
};
</script>

<style scoped>
.form-control,
.form-select {
  border: 1px solid #d2d6da !important;
}

.form-control:focus,
.form-select:focus {
  border-color: #4caf50 !important;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2) !important;
}
</style>
